import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Cake, Confetti } from "@phosphor-icons/react";

/** Birthdays This Month + Upcoming.
 *  Privacy: only month and day are shown — the API never returns the birth
 *  year or an age, and this component never computes one.
 */
export const BirthdaysCard = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        api.get("/birthdays")
            .then(({ data: d }) => { if (!cancelled) setData(d); })
            .catch(() => { if (!cancelled) setData(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const today = Array.isArray(data?.today) ? data.today : [];
    const thisMonth = Array.isArray(data?.this_month) ? data.this_month : [];
    const upcoming = Array.isArray(data?.upcoming) ? data.upcoming : [];
    const todayIds = new Set(today.map((t) => t.id));

    return (
        <Card className="border-[#E2E8F0]" data-testid="birthdays-card">
            <CardContent className="p-6 space-y-5">
                <div className="flex items-center justify-between gap-3">
                    <h2 className="text-base md:text-lg font-semibold text-[#003366] flex items-center gap-2"
                        style={{ fontFamily: "Outfit" }}>
                        <Cake size={18} className="text-[#E6002C]" />
                        {data?.month_name ? `${data.month_name} Birthdays` : "Birthdays This Month"}
                    </h2>
                    {thisMonth.length > 0 && (
                        <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px] uppercase tracking-wider">
                            {thisMonth.length} this month
                        </Badge>
                    )}
                </div>

                {/* Birthday today */}
                {today.map((p) => (
                    <div
                        key={p.id}
                        className="rounded-xl border-2 border-[#E6002C]/30 bg-gradient-to-r from-[#E6002C]/8 to-transparent px-4 py-3.5 flex items-center gap-3"
                        data-testid={`birthday-today-${p.id}`}
                    >
                        <span className="text-2xl leading-none" aria-hidden="true">🎂</span>
                        <div className="min-w-0">
                            <div className="font-bold text-[#003366]">Happy Birthday, {p.full_name}!</div>
                            <div className="text-xs text-slate-500">{p.label}{p.position ? ` · ${p.position}` : ""}</div>
                        </div>
                    </div>
                ))}

                {loading ? (
                    <div className="py-6 text-center text-sm text-slate-500">Loading birthdays…</div>
                ) : thisMonth.length === 0 && upcoming.length === 0 ? (
                    <div className="py-6 text-center" data-testid="birthdays-empty">
                        <p className="text-sm text-slate-500">
                            No birthdays recorded yet.
                        </p>
                        {isAdmin ? (
                            <p className="text-xs text-slate-400 mt-1">
                                Add a date of birth from Technicians → edit an employee.
                            </p>
                        ) : (
                            <p className="text-xs text-slate-400 mt-1">
                                Add yours from{" "}
                                <Link to="/profile" className="text-[#0066D9] underline"
                                    data-testid="birthdays-profile-link">My Profile</Link>{" "}
                                so the team can celebrate with you.
                            </p>
                        )}
                    </div>
                ) : (
                    <>
                        {thisMonth.length > 0 && (
                            <div className="divide-y divide-slate-100" data-testid="birthdays-this-month">
                                {thisMonth.map((p) => (
                                    <div key={p.id} className="py-2.5 flex items-center justify-between gap-3"
                                        data-testid={`birthday-month-row-${p.id}`}>
                                        <div className="flex items-center gap-2 min-w-0">
                                            <span aria-hidden="true">{todayIds.has(p.id) ? "🎂" : "🎉"}</span>
                                            <span className="font-medium text-[#003366] truncate">{p.full_name}</span>
                                        </div>
                                        <span className="text-sm text-slate-600 whitespace-nowrap">{p.label}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {upcoming.length > 0 && (
                            <div className="pt-1" data-testid="birthdays-upcoming">
                                <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">
                                    <Confetti size={14} /> Upcoming birthdays
                                </div>
                                <div className="divide-y divide-slate-50">
                                    {upcoming.map((p) => (
                                        <div key={`up-${p.id}`} className="py-2 flex items-center justify-between gap-3 text-sm"
                                            data-testid={`birthday-upcoming-row-${p.id}`}>
                                            <span className="text-slate-500 font-mono whitespace-nowrap w-16">{p.short_label}</span>
                                            <span className="flex-1 text-slate-700 truncate">{p.full_name}</span>
                                            <span className="text-xs text-slate-400 whitespace-nowrap">
                                                in {p.days_until} {p.days_until === 1 ? "day" : "days"}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {(data?.missing_dob || 0) > 0 && (
                    <p className="text-xs text-slate-400 pt-1" data-testid="birthdays-missing-count">
                        {data.missing_dob} employee{data.missing_dob === 1 ? "" : "s"} without a date of birth on file.
                    </p>
                )}
            </CardContent>
        </Card>
    );
};
